import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { NamedLink } from '../NamedLink/NamedLink';
import css from './BlogCard.module.css';

const formatDate = (dateStr, intl) => {
  if (!dateStr) return null;
  try {
    return intl.formatDate(new Date(dateStr), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
};

const normalizeUrlPath = urlPath => {
  if (!urlPath || typeof urlPath !== 'string') return '';
  const trimmed = urlPath.trim();
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const BlogCard = ({ post, featured = false }) => {
  if (!post) return null;

  const intl = useIntl();
  const date = formatDate(post.published_at, intl);
  const category = Array.isArray(post.categories) && post.categories.length > 0
    ? post.categories[0]
    : null;

  const cardClass = featured ? `${css.card} ${css.cardFeatured}` : css.card;

  const customPath = post.url_path || post.urlPath;
  const LinkWrapper = customPath
    ? ({ children, className }) => (
        <Link to={{ pathname: normalizeUrlPath(customPath) }} className={className}>{children}</Link>
      )
    : ({ children, className }) => (
        <NamedLink name="BlogPostPage" params={{ slug: post.slug }} className={className}>{children}</NamedLink>
      );

  return (
    <LinkWrapper className={css.cardLink}>
      <article className={cardClass}>
        {post.featured_image_url ? (
          <div className={css.imageWrapper}>
            <img
              src={post.featured_image_url}
              alt={post.title}
              className={css.image}
              loading="lazy"
            />
            {category ? (
              <span className={css.categoryBadge}>{category.name}</span>
            ) : null}
          </div>
        ) : (
          <div className={css.imagePlaceholder}>
            <svg className={css.placeholderIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            {category ? (
              <span className={css.categoryBadge}>{category.name}</span>
            ) : null}
          </div>
        )}

        <div className={css.body}>
          <h2 className={css.title}>{post.title}</h2>

          {post.excerpt ? (
            <p className={css.excerpt}>{post.excerpt}</p>
          ) : null}

          <div className={css.footer}>
            <div className={css.meta}>
              {date ? <time className={css.date}>{date}</time> : null}
              {post.reading_time ? (
                <span className={css.readingTime}>{post.reading_time} min</span>
              ) : null}
            </div>
            <span className={css.readMore}>
              <FormattedMessage id="BlogCard.readMore" />
              <span className={css.arrow}>→</span>
            </span>
          </div>
        </div>
      </article>
    </LinkWrapper>
  );
};

export default BlogCard;
